import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaShoppingCart, FaTruck, FaFacebookF, FaWhatsapp, FaEnvelope, FaRegCommentDots } from 'react-icons/fa';
/* ... existing imports ... */


import { getProductBySlug } from '../data/productsData';
import { getProductDisplayInfo } from '../utils/productUtils';
import { addToCart } from '../utils/cartUtils';
import { useToast } from '../components/ToastProvider';
import PincodeChecker from '../components/PincodeChecker';
import './ProductDetail.css';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = getProductBySlug(slug);

  // State for variants/sizes
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [currentPrice, setCurrentPrice] = useState(0);

  const [addedToCart, setAddedToCart] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { showToast } = useToast();

  // Get all available images for the product
  const allImages = product?.images || [product?.image].filter(Boolean);

  // Initialize variant/size selection
  useEffect(() => {
    if (product) {
      const { displayPrice, lowestPriceVariant } = getProductDisplayInfo(product);
      setSelectedVariant(lowestPriceVariant);
      setCurrentPrice(lowestPriceVariant ? lowestPriceVariant.price : (product.price || 0));
    }
  }, [product]);

  useEffect(() => {
    // Scroll to top immediately when component mounts or slug changes
    window.scrollTo(0, 0);

    // Update document title for SEO
    if (product) {
      document.title = product.metaTitle || `${product.name} - Handmade Haven`;

      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', product.metaDescription || product.description);
      }
    }
  }, [product, slug]);

  const handleVariantChange = (e) => {
    const idx = parseInt(e.target.value);
    const options = product.type === 'variant' ? product.variants : product.sizes;
    if (options && options[idx]) {
      setSelectedVariant(options[idx]);
      setCurrentPrice(options[idx].price);
    }
  };

  const clampQuantity = (value) => {
    if (Number.isNaN(value)) return 1;
    if (value < 1) return 1;
    if (value > 99) return 99;
    return value;
  };

  const handleQuantityInput = (e) => {
    const value = clampQuantity(parseInt(e.target.value, 10));
    setQuantity(value);
  };

  const handleIncrement = () => {
    setQuantity((prev) => clampQuantity(prev + 1));
  };

  const handleDecrement = () => {
    setQuantity((prev) => clampQuantity(prev - 1));
  };

  const handleAddToCart = () => {
    if (product) {
      try {
        // Pass selectedVariant and quantity to new cartUtils
        addToCart(product, selectedVariant, quantity);
        setAddedToCart(true);
        window.dispatchEvent(new Event('cartUpdated'));
        showToast('Added to cart', 'success');

        setTimeout(() => {
          setAddedToCart(false);
        }, 2000);
      } catch (err) {
        showToast('Could not add to cart', 'error');
        console.error(err);
      }
    }
  };

  // WhatsApp ordering handler
  const handleWhatsAppOrder = () => {
    if (product && product.orderType === 'whatsapp') {
      const message = `Hi! I'm interested in ordering *${product.name}*.\n\nProduct: ${product.name}\nCustomization: ${product.shortDescription}\n\nPlease provide pricing and customization details.`;
      const whatsappLink = `https://wa.me/${product.whatsappNumber}?text=${encodeURIComponent(message)}`;
      window.open(whatsappLink, '_blank', 'noopener,noreferrer');
    }
  };

  const handleOrderNow = () => {
    if (product) {
      try {
        addToCart(product, selectedVariant, quantity);
        window.dispatchEvent(new Event('cartUpdated'));
        showToast('Added to cart', 'success');
        navigate('/cart');
      } catch (err) {
        showToast('Could not add to cart', 'error');
      }
    }
  };

  // If product not found, redirect to home
  if (!product) {
    navigate('/');
    return null;
  }

  // Determine if we need a dropdown
  const hasOptions = (product.type === 'variant' || product.type === 'size') &&
    (product.variants || product.sizes);
  const options = product.type === 'variant' ? product.variants : product.sizes;

  return (
    <div className="product-detail-page">
      {/* Breadcrumb Navigation */}
      <motion.nav
        className="breadcrumb"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="container">
          <Link to="/" className="breadcrumb-link">Home</Link>
          <span className="breadcrumb-separator">/</span>
          <Link to="/#products" className="breadcrumb-link">Products</Link>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{product.name}</span>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="product-hero">
        <div className="container">
          <motion.div
            className="back-button"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <button onClick={() => navigate(-1)} className="btn-back">
              <FaArrowLeft />
              Back to Products
            </button>
          </motion.div>

          <div className="product-hero-content">
            <motion.div
              className="product-hero-image"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="product-image-wrapper">
                <img
                  src={allImages[currentImageIndex] || selectedVariant?.image || product.image}
                  alt={`${product.name} ${selectedVariant?.label ? `(${selectedVariant.label})` : ''}`}
                  fetchpriority="high"
                  decoding="async"
                  key={allImages[currentImageIndex] || selectedVariant?.image || product.image}
                />

                {/* Image Gallery Navigation */}
                {allImages.length > 1 && (
                  <div className="image-gallery-nav">
                    <button
                      className="gallery-nav-btn prev"
                      onClick={() => setCurrentImageIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1))}
                      aria-label="Previous image"
                    >
                      ‹
                    </button>
                    <div className="gallery-dots">
                      {allImages.map((_, idx) => (
                        <button
                          key={idx}
                          className={`gallery-dot ${idx === currentImageIndex ? 'active' : ''}`}
                          onClick={() => setCurrentImageIndex(idx)}
                          aria-label={`View image ${idx + 1}`}
                        />
                      ))}
                    </div>
                    <button
                      className="gallery-nav-btn next"
                      onClick={() => setCurrentImageIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1))}
                      aria-label="Next image"
                    >
                      ›
                    </button>
                  </div>
                )}

                {/* Thumbnail Gallery */}
                {allImages.length > 1 && (
                  <div className="image-thumbnails">
                    {allImages.map((img, idx) => (
                      <img
                        key={idx}
                        src={img}
                        alt={`${product.name} thumbnail ${idx + 1}`}
                        className={`thumbnail ${idx === currentImageIndex ? 'active' : ''}`}
                        onClick={() => setCurrentImageIndex(idx)}
                        decoding="async"
                        loading="lazy"
                      />
                    ))}
                  </div>
                )}
              </div>
            </motion.div>

            <motion.div
              className="product-hero-info"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {/* --- A. Product Core --- */}
              <div className="product-core-info">
                <h1 className="product-title">{product.name}</h1>
                {product.orderType !== 'whatsapp' && (
                  <div className="product-price-wrapper">
                    <div className="product-price-detail">₹ {currentPrice}</div>
                    <div className="product-badges">
                      <span className="badge badge-soft">Handcrafted</span>
                      <span className="badge badge-soft">Premium Quality</span>
                    </div>
                  </div>
                )}
                {product.orderType === 'whatsapp' && (
                  <div className="product-badges" style={{ marginTop: '12px' }}>
                    <span className="badge badge-soft">Handcrafted</span>
                    <span className="badge badge-soft">Premium Quality</span>
                    <span className="badge badge-whatsapp" style={{ background: '#25D366', color: '#fff' }}>
                      <FaWhatsapp style={{ marginRight: '4px' }} /> WhatsApp Order
                    </span>
                  </div>
                )}
                {product.orderType !== 'whatsapp' && (
                  <p className="product-short-description">{product.shortDescription || product.description}</p>
                )}
                {product.orderType === 'whatsapp' && (
                  <div className="whatsapp-product-notice">
                    <FaRegCommentDots style={{ marginRight: '8px', color: '#25D366' }} />
                    <p style={{ margin: 0 }}>
                      <strong>Custom Product:</strong> {product.shortDescription || product.description}
                      <br />
                      <span style={{ fontSize: '0.9em', color: '#666' }}>Click below to discuss your requirements on WhatsApp</span>
                    </p>
                  </div>
                )}
              </div>

              {/* --- B. Purchase Actions --- */}
              <div className="product-purchase-actions">
                {/* Variant / Size Selector */}
                {hasOptions && product.orderType !== 'whatsapp' && (
                  <div className="product-variant-selector">
                    <label htmlFor="variant-select" className="variant-select-label">
                      {product.type === 'variant' ? 'Select Option' : 'Select Size'}
                    </label>
                    <div className="select-wrapper">
                      <select
                        id="variant-select"
                        onChange={handleVariantChange}
                        className="variant-dropdown"
                      >
                        {options.map((opt, idx) => (
                          <option key={idx} value={idx}>
                            {opt.label} - ₹{opt.price}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Quantity and CTAs */}
                {product.orderType !== 'whatsapp' && (
                  <div className="action-row">
                    <div className="quantity-wrapper">
                      <label className="quantity-label">Quantity</label>
                      <div className="quantity-selector" aria-label="Select quantity">
                        <motion.button
                          type="button"
                          className="quantity-btn"
                          onClick={handleDecrement}
                          disabled={quantity <= 1}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          -
                        </motion.button>
                        <input
                          type="number"
                          min="1"
                          max="99"
                          aria-label="Product quantity"
                          className="quantity-input"
                          value={quantity}
                          onChange={handleQuantityInput}
                        />
                        <motion.button
                          type="button"
                          className="quantity-btn"
                          onClick={handleIncrement}
                          disabled={quantity >= 99}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          +
                        </motion.button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Conditional: Cart/Order buttons OR WhatsApp button */}
                {product.orderType === 'whatsapp' ? (
                  <div className="cta-group">
                    <motion.a
                      href={`https://wa.me/${product.whatsappNumber}?text=${encodeURIComponent(`Hi! I'm interested in ordering *${product.name}*.\n\nProduct: ${product.name}\nCustomization: ${product.shortDescription}\n\nPlease provide pricing and customization details.`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-whatsapp-order"
                      style={{
                        background: '#25D366',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '14px 24px',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        fontWeight: '600',
                        fontSize: '16px'
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <FaWhatsapp size={20} />
                      Order via WhatsApp
                    </motion.a>
                  </div>
                ) : (
                  <div className="cta-group">
                    <motion.button
                      className="btn-primary-action"
                      onClick={handleAddToCart}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      animate={{
                        scale: addedToCart ? [1, 1.05, 1] : 1
                      }}
                    >
                      <FaShoppingCart />
                      {addedToCart ? 'Added to Cart!' : 'Add to Cart'}
                    </motion.button>

                    <motion.button
                      className="btn-secondary-action"
                      onClick={handleOrderNow}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Order Now
                    </motion.button>
                  </div>
                )}

                {product.orderType !== 'whatsapp' && (
                  <div className="shipping-note">
                    <FaTruck className="shipping-icon" />
                    <span>Fast Shipping • 100% Satisfaction Guarantee</span>
                  </div>
                )}
              </div>


              {/* --- C. Trust & Logistics --- */}
              <div className="product-trust-logistics">
                {/* Guaranteed Safe Checkout (Refined) */}
                <div className="safe-checkout-container">
                  <img
                    src="/Guaranteed safe checkout.webp"
                    alt="Guaranteed Safe Checkout"
                    className="safe-checkout-img"
                  />
                </div>

                {/* Pincode Availability Checker */}
                <div className="delivery-checker-wrapper">
                  <PincodeChecker />
                </div>

                {/* Share Buttons (Demoted) */}
                <div className="share-buttons-container">
                  <span className="share-label">Share:</span>
                  <button className="share-btn facebook" onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')} aria-label="Share on Facebook">
                    <FaFacebookF />
                  </button>
                  <button className="share-btn whatsapp" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(product.name + ' - ' + window.location.href)}`, '_blank')} aria-label="Share on WhatsApp">
                    <FaWhatsapp />
                  </button>
                  <button className="share-btn email" onClick={() => window.location.href = `mailto:?subject=${encodeURIComponent(product.name)}&body=${encodeURIComponent('Check out this product: ' + window.location.href)}`} aria-label="Share via Email">
                    <FaEnvelope />
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Description Section */}
      <section className="product-description-section">
        <div className="container">
          <motion.div
            className="product-description-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-heading">About This Product</h2>
            <p className="product-full-description">{product.description}</p>

            <div className="product-features">
              <h3>Key Features</h3>
              <ul>
                <li>Handcrafted with love and attention to detail</li>
                <li>Premium quality materials</li>
                <li>Unique and elegant design</li>
                <li>Perfect for gifting or personal use</li>
              </ul>
            </div>

            <motion.div
              className="cta-section"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <motion.button
                className="btn-order-whatsapp-large"
                onClick={handleOrderNow}
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaShoppingCart />
                Order Now
              </motion.button>
              <p className="cta-note">DM us for customizations and bulk orders</p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;


